import { useEffect, useRef, useState } from 'react';
import { podeFazer } from '../../acesso';
import type { Eu } from '../../acesso';
import type { Sessao } from '../../auth';
import {
  buscarAgoraNoHos,
  lerAppDoBalconista,
  lerConfiguracoes,
  lerHistoricoDaConfiguracao,
  lerDevolucoesParaRevisar,
  lerSincronizadorHos,
  revisarDevolucao,
  salvarAppDoBalconista,
  salvarConfiguracaoGeral,
  salvarSincronizadorHos,
} from '../../administracao';
import type {
  AppDoBalconista,
  AreaDeConfiguracao,
  DevolucaoParaRevisar,
  EstadoDoSincronizadorHos,
  VisaoDasConfiguracoes,
} from '../../administracao';
import { EstadoVazio, ErroDeArea, SemAcesso } from '../../design/Estados';
import { Icone } from '../../design/Icone';
import type { NomeDoIcone } from '../../design/Icone';
import {
  IconeLocal,
  SeloDoAmbiente,
  diaHora,
  diaMes,
  hora,
  useLeitura,
  useVersao,
} from './sistema-comum';
import './config.css';

/**
 * TELA 34 — CONFIGURAÇÕES DO SISTEMA.
 *
 *   "Organiza, aponta e mostra estado. Não duplica editor."
 *
 * - Cada área mostra o resumo vigente que o servidor calculou e o link para o editor do módulo.
 * - Só o que é realmente geral se edita aqui: o nome da operação. Fuso e moeda são informativos.
 * - Salvar exige motivo e só confirma depois do servidor. Conflito de versão (409) pede recarga;
 *   sem resposta é INCERTO (pode ter gravado) — nunca apresentado como recusa.
 * - Restaurar grava uma versão NOVA com o valor antigo: o que veio no meio continua no histórico.
 */

const ICONE_DA_AREA: Record<AreaDeConfiguracao['area'], NomeDoIcone | 'plug'> = {
  geral: 'cog',
  giro: 'cycle',
  comercial: 'money',
  metas: 'gauge',
  qualidade: 'shield',
  alertas: 'bell',
  acesso: 'users',
  integracoes: 'plug',
  dados: 'db',
};

function IconeDaArea({
  area,
  tamanho = 17,
}: {
  area: AreaDeConfiguracao['area'];
  tamanho?: number;
}) {
  const n = ICONE_DA_AREA[area];
  return n === 'plug' ? (
    <IconeLocal nome="plug" tamanho={tamanho} traco={1.5} />
  ) : (
    <Icone nome={n} tamanho={tamanho} traco={1.5} />
  );
}

export function Configuracoes({
  sessao,
  eu,
  parametros,
}: {
  sessao: Sessao;
  eu: Eu | null;
  parametros: URLSearchParams;
}) {
  // Recarregar NÃO apaga o que está na tela (o desfecho do salvar continua visível).
  const visao = useLeitura(lerConfiguracoes, 'visao');
  const versao = useVersao();
  const campo = useRef<HTMLInputElement>(null);
  const verHistorico = parametros.get('historico') === '1';
  const recarregar = visao.recarregar;

  if (visao.falha === 'sem-acesso') return <SemAcesso area="Configurações do sistema" />;

  return (
    <section className="cg">
      <div className="cg-faixa">
        <span className="cg-faixa-texto">
          Aqui se editam o nome da operação e o aplicativo do balconista · cada área abre o editor
          do próprio módulo
        </span>
        <span className="cg-faixa-espaco" />
        <SeloDoAmbiente v={versao.v} falhou={versao.falhou} />
      </div>
      {!visao.d ? (
        visao.falha ? (
          <ErroDeArea oQue="as configurações" aoTentar={visao.recarregar} />
        ) : (
          <div className="cg-carregando" aria-busy="true" />
        )
      ) : (
        <div className="cg-grade">
          <Areas visao={visao.d} aoAbrirGeral={() => campo.current?.focus()} />
          <div className="cg-coluna">
            <PainelGeral
              sessao={sessao}
              geral={visao.d.geral}
              campo={campo}
              aoMudar={recarregar}
              verHistorico={verHistorico}
            />
            <PainelDoBalconista sessao={sessao} aoMudar={recarregar} />
            {/* Só os donos da loja (loja.ver). Quem decide é o servidor; aqui só esconde. */}
            {podeFazer(eu, 'loja.ver') && <PainelDoHos sessao={sessao} aoMudar={recarregar} />}
            {verHistorico ? (
              <HistoricoGeral
                sessao={sessao}
                versaoAtual={visao.d.geral.versao}
                valorAtual={visao.d.geral.nomeDaOperacao}
                aoMudar={recarregar}
              />
            ) : (
              <MudancasRecentes mudancas={visao.d.mudancas} />
            )}
          </div>
        </div>
      )}
    </section>
  );
}

/* ------------------------------ lista densa por área ------------------------------ */

function Areas({
  visao,
  aoAbrirGeral,
}: {
  visao: VisaoDasConfiguracoes;
  aoAbrirGeral: () => void;
}) {
  return (
    <div className="cg-cartao cg-areas">
      <div className="cg-cartao-topo">
        <h1>Configurações por área</h1>
        <span>Resumo do que está vigente · o editor fica no módulo</span>
      </div>
      <ul className="cg-lista">
        {visao.areas.map((a) => {
          const aqui = a.area === 'geral';
          return (
            <li key={a.area}>
              <a
                className={`cg-area${a.alerta ? ' cg-area-alerta' : ''}`}
                href={a.link}
                onClick={
                  aqui
                    ? (e) => {
                        e.preventDefault();
                        aoAbrirGeral();
                      }
                    : undefined
                }
              >
                <span className="cg-area-icone" aria-hidden="true">
                  <IconeDaArea area={a.area} />
                </span>
                <span className="cg-area-texto">
                  <span className="cg-area-titulo">{a.titulo}</span>
                  <span className="cg-area-resumo">{a.resumo}</span>
                </span>
                <span className={`cg-area-acao${aqui ? ' cg-area-aqui' : ''}`}>{a.acao}</span>
                <Icone nome="chev-r" tamanho={15} traco={1.7} />
              </a>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/* ------------------------------ painel Geral (editável) ------------------------------ */

type Desfecho =
  | { readonly t: 'nada' }
  | { readonly t: 'salvando' }
  | { readonly t: 'salvo'; readonly as: string; readonly versao: number }
  | { readonly t: 'falha'; readonly msg: string }
  | { readonly t: 'conflito'; readonly msg: string }
  | { readonly t: 'incerta' };

/** Os estados de salvamento do design, cada um com o seu texto — nunca "sucesso" antes do servidor. */
function FaixaDoDesfecho({ d, aoRecarregar }: { d: Desfecho; aoRecarregar: () => void }) {
  if (d.t === 'salvo')
    return (
      <div className="cg-estado cg-estado-ok" role="status">
        <Icone nome="check" tamanho={15} traco={2.2} />
        <span>Salvo · confirmado pelo servidor às {d.as}</span>
      </div>
    );
  if (d.t === 'falha')
    return (
      <div className="cg-estado cg-estado-erro" role="alert">
        <Icone nome="warn" tamanho={15} traco={1.8} />
        <span>
          Falha ao salvar · nada foi alterado
          {d.msg && <span className="cg-estado-msg">Mensagem do servidor: “{d.msg}”</span>}
        </span>
      </div>
    );
  if (d.t === 'conflito')
    return (
      <div className="cg-estado cg-estado-info" role="alert">
        <span>Este conteúdo foi atualizado por outro usuário</span>
        <button type="button" className="cg-botao-info" onClick={aoRecarregar}>
          Recarregar
        </button>
      </div>
    );
  if (d.t === 'incerta')
    return (
      <div className="cg-estado cg-estado-aviso" role="alert">
        <span>
          Sem resposta do servidor: a mudança pode ter sido gravada. Recarregue antes de repetir.
        </span>
        <button type="button" className="cg-botao-info" onClick={aoRecarregar}>
          Recarregar
        </button>
      </div>
    );
  if (d.t === 'salvando')
    return (
      <div className="cg-estado" role="status">
        <span className="cg-ponto cg-ponto-cinza" />
        <span>Salvando…</span>
        <span className="cg-estado-dir">aguardando o servidor</span>
      </div>
    );
  return null;
}

function PainelGeral({
  sessao,
  geral,
  campo,
  aoMudar,
  verHistorico,
}: {
  sessao: Sessao;
  geral: VisaoDasConfiguracoes['geral'];
  campo: React.RefObject<HTMLInputElement | null>;
  aoMudar: () => void;
  verHistorico: boolean;
}) {
  const [nome, setNome] = useState(geral.nomeDaOperacao);
  const [motivo, setMotivo] = useState('');
  const [d, setD] = useState<Desfecho>({ t: 'nada' });
  // Chegou versão nova do servidor (salvou, restaurou ou recarregou): o campo volta ao vigente.
  useEffect(() => {
    setNome(geral.nomeDaOperacao);
    setD((x) => (x.t === 'salvo' ? x : { t: 'nada' }));
  }, [geral.versao, geral.nomeDaOperacao]);
  const mudou = nome.trim() !== geral.nomeDaOperacao;
  const salvando = d.t === 'salvando';
  const [fuso, offset] = geral.fuso.split(' · ');

  const salvar = () => {
    if (nome.trim().length < 3 || nome.trim().length > 60)
      return setD({ t: 'falha', msg: 'Nome da operação: de 3 a 60 caracteres.' });
    if (motivo.trim().length < 5)
      return setD({
        t: 'falha',
        msg: 'Diga por que está mudando (mínimo 5 caracteres). Fica no histórico.',
      });
    setD({ t: 'salvando' });
    void salvarConfiguracaoGeral(sessao, {
      valor: nome.trim(),
      motivo: motivo.trim(),
      versaoAtual: geral.versao,
    }).then((r) => {
      if (r.tipo === 'confirmada') {
        setD({ t: 'salvo', as: hora(new Date().toISOString()), versao: r.dados.versao });
        setMotivo('');
        aoMudar();
      } else if (r.tipo === 'recusada')
        setD(
          r.status === 409 ? { t: 'conflito', msg: r.mensagem } : { t: 'falha', msg: r.mensagem },
        );
      else setD({ t: 'incerta' });
    });
  };

  return (
    <div className="cg-cartao cg-geral">
      <div className="cg-cartao-topo cg-cartao-topo-linha">
        <h2>Geral</h2>
        {salvando ? (
          <span className="cg-marca">
            <span className="cg-ponto cg-ponto-cinza" />
            Salvando…
          </span>
        ) : mudou ? (
          <span className="cg-marca cg-marca-aviso">
            <span className="cg-ponto" />
            Alterações não salvas
          </span>
        ) : (
          <a
            className="cg-marca cg-marca-link"
            href={verHistorico ? '#/configuracoes' : '#/configuracoes?historico=1'}
          >
            {verHistorico ? 'Mudanças recentes' : `Histórico · v${String(geral.versao)}`}
          </a>
        )}
      </div>
      <div className="cg-geral-corpo">
        <label className="cg-rotulo" htmlFor="cg-nome">
          Nome da operação <span className="cg-editavel">· editável</span>
        </label>
        <input
          id="cg-nome"
          ref={campo}
          className={`cg-input${mudou ? ' cg-input-mudou' : ''}`}
          value={nome}
          maxLength={60}
          disabled={salvando}
          onChange={(e) => {
            setNome(e.target.value);
            if (d.t !== 'salvando') setD({ t: 'nada' });
          }}
        />
        <div className="cg-duas">
          <div>
            <div className="cg-rotulo">
              Fuso horário <span className="cg-info">· informativo</span>
            </div>
            <div className="cg-fixo" title={geral.fuso}>
              <Icone nome="lock" tamanho={13} traco={1.7} />
              <span>
                {fuso?.split(' (')[0] ?? geral.fuso}
                {offset ? ` · ${offset}` : ''}
              </span>
            </div>
          </div>
          <div>
            <div className="cg-rotulo">
              Moeda <span className="cg-info">· informativo</span>
            </div>
            <div className="cg-fixo" title={`${geral.moeda} · data ${geral.formatoDeData}`}>
              <Icone nome="lock" tamanho={13} traco={1.7} />
              <span>{geral.moeda}</span>
            </div>
          </div>
        </div>
        <div className="cg-previa">
          <span className="cg-previa-antes">{mudou ? geral.nomeDaOperacao : 'Nome vigente'}</span>
          <Icone nome="chev-r" tamanho={13} traco={1.8} />
          <span className="cg-previa-depois">
            {mudou ? nome.trim() || '—' : geral.nomeDaOperacao}
          </span>
        </div>
        {mudou && (
          <label className="cg-motivo">
            <span className="cg-rotulo">Por que está mudando? · fica no histórico</span>
            <input
              className="cg-input cg-input-motivo"
              value={motivo}
              maxLength={300}
              disabled={salvando}
              placeholder="Ex.: nome oficial da unidade"
              onChange={(e) => setMotivo(e.target.value)}
            />
          </label>
        )}
        <div className="cg-botoes">
          <button
            type="button"
            className="cg-primario"
            disabled={!mudou || salvando}
            onClick={salvar}
          >
            {salvando ? 'Salvando…' : 'Salvar'}
          </button>
          <button
            type="button"
            className="cg-secundario"
            disabled={!mudou || salvando}
            onClick={() => {
              setNome(geral.nomeDaOperacao);
              setMotivo('');
              setD({ t: 'nada' });
            }}
          >
            Descartar
          </button>
        </div>
        <FaixaDoDesfecho
          d={d}
          aoRecarregar={() => {
            setD({ t: 'nada' });
            aoMudar();
          }}
        />
      </div>
    </div>
  );
}

/* ------------------------------ mudanças recentes ------------------------------ */

function MudancasRecentes({ mudancas }: { mudancas: VisaoDasConfiguracoes['mudancas'] }) {
  return (
    <div className="cg-cartao cg-mudancas">
      <div className="cg-cartao-topo cg-cartao-topo-linha">
        <h2>Mudanças recentes</h2>
        <a className="cg-marca cg-marca-link" href="#/configuracoes?historico=1">
          Histórico · Geral
        </a>
      </div>
      {mudancas.length === 0 ? (
        <EstadoVazio
          icone="clock"
          titulo="Nenhuma mudança registrada"
          texto="Quando alguém alterar uma configuração, ela aparece aqui com autor, data e motivo."
        />
      ) : (
        <ul className="cg-mud">
          {mudancas.map((m) => (
            <li key={`${m.chave}-${String(m.versao)}`}>
              <span className="cg-mud-quando">
                {diaMes(m.em)}
                <br />
                {hora(m.em)}
              </span>
              <span className="cg-mud-texto">
                <b>{m.por ?? 'Sistema'}</b> · {m.rotulo} (v{m.versao})
                {m.motivo && <span className="cg-mud-motivo">{m.motivo}</span>}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ------------------------------ histórico e restauração ------------------------------ */

const valorTexto = (v: unknown) => (typeof v === 'string' ? v : JSON.stringify(v));

function HistoricoGeral({
  sessao,
  versaoAtual,
  valorAtual,
  aoMudar,
}: {
  sessao: Sessao;
  versaoAtual: number;
  valorAtual: string;
  aoMudar: () => void;
}) {
  const hist = useLeitura(
    () => lerHistoricoDaConfiguracao('operacao_nome'),
    `hist-${String(versaoAtual)}`,
  );
  const [escolhida, setEscolhida] = useState<number | null>(null);
  const [confirmando, setConfirmando] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [d, setD] = useState<Desfecho>({ t: 'nada' });
  const versoes = hist.d?.versoes ?? [];
  const alvo = versoes.find((v) => v.versao === escolhida) ?? null;

  const restaurar = () => {
    if (!alvo) return;
    if (motivo.trim().length < 5)
      return setD({
        t: 'falha',
        msg: 'Diga por que está restaurando (mínimo 5 caracteres). Fica no histórico.',
      });
    setD({ t: 'salvando' });
    void salvarConfiguracaoGeral(sessao, {
      restaurarVersao: alvo.versao,
      motivo: motivo.trim(),
      versaoAtual,
    }).then((r) => {
      if (r.tipo === 'confirmada') {
        setD({ t: 'salvo', as: hora(new Date().toISOString()), versao: r.dados.versao });
        setEscolhida(null);
        setConfirmando(false);
        setMotivo('');
        aoMudar();
      } else if (r.tipo === 'recusada')
        setD(
          r.status === 409 ? { t: 'conflito', msg: r.mensagem } : { t: 'falha', msg: r.mensagem },
        );
      else setD({ t: 'incerta' });
    });
  };

  return (
    <div className="cg-cartao cg-hist">
      <div className="cg-cartao-topo cg-cartao-topo-linha">
        <div>
          <h2>Histórico · Geral</h2>
          <span>Antes, depois, autor e data</span>
        </div>
        <a className="cg-marca cg-marca-link" href="#/configuracoes">
          Mudanças recentes
        </a>
      </div>
      <div className="cg-hist-corpo">
        {!hist.d ? (
          hist.falha ? (
            <ErroDeArea oQue="o histórico" aoTentar={hist.recarregar} />
          ) : (
            <div className="cg-carregando cg-carregando-curto" aria-busy="true" />
          )
        ) : versoes.length === 0 ? (
          <EstadoVazio
            icone="clock"
            titulo="Nunca alterado"
            texto={`O nome da operação ainda é o padrão (“${valorAtual}”). A primeira mudança salva cria a versão 1.`}
          />
        ) : (
          <>
            <ul className="cg-hist-lista" role="radiogroup" aria-label="Versões">
              {versoes.map((v, i) => {
                const antes = versoes[i + 1];
                const atual = v.versao === versaoAtual;
                const sel = v.versao === escolhida;
                return (
                  <li key={v.versao}>
                    <button
                      type="button"
                      role="radio"
                      aria-checked={sel}
                      className={`cg-hist-item${sel ? ' cg-hist-sel' : ''}`}
                      disabled={atual || d.t === 'salvando'}
                      onClick={() => {
                        setEscolhida(v.versao);
                        setConfirmando(false);
                        setD({ t: 'nada' });
                      }}
                    >
                      <span className="cg-hist-linha">
                        <span className="cg-hist-quando">{diaHora(v.em)}</span>
                        <span className="cg-hist-quem">
                          <b>{v.por ?? 'Sistema'}</b> · {antes ? v.motivo : 'configuração inicial'}
                        </span>
                        <span className="cg-hist-v">
                          {atual ? `v${String(v.versao)} · vigente` : `v${String(v.versao)}`}
                        </span>
                      </span>
                      <span className="cg-hist-valor">
                        {antes
                          ? `${valorTexto(antes.valor)} → ${valorTexto(v.valor)}`
                          : valorTexto(v.valor)}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
            {!confirmando ? (
              <button
                type="button"
                className="cg-secundario cg-largo"
                disabled={!alvo || valorTexto(alvo.valor) === valorAtual}
                onClick={() => setConfirmando(true)}
              >
                {alvo
                  ? `Restaurar versão de ${diaMes(alvo.em)} (v${String(alvo.versao)})`
                  : 'Escolha uma versão anterior para restaurar'}
              </button>
            ) : (
              alvo && (
                <div className="cg-restaurar">
                  <div className="cg-restaurar-titulo">
                    Restaurar “{valorTexto(alvo.valor)}” (v{alvo.versao})
                  </div>
                  <label className="cg-motivo">
                    <span className="cg-rotulo">Por que está restaurando? · fica no histórico</span>
                    <input
                      className="cg-input cg-input-motivo"
                      value={motivo}
                      maxLength={300}
                      disabled={d.t === 'salvando'}
                      onChange={(e) => setMotivo(e.target.value)}
                    />
                  </label>
                  <div className="cg-botoes">
                    <button
                      type="button"
                      className="cg-primario"
                      disabled={d.t === 'salvando'}
                      onClick={restaurar}
                    >
                      {d.t === 'salvando' ? 'Restaurando…' : 'Criar nova versão com este valor'}
                    </button>
                    <button
                      type="button"
                      className="cg-secundario"
                      disabled={d.t === 'salvando'}
                      onClick={() => {
                        setConfirmando(false);
                        setMotivo('');
                        setD({ t: 'nada' });
                      }}
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              )
            )}
            <FaixaDoDesfecho
              d={d}
              aoRecarregar={() => {
                setD({ t: 'nada' });
                aoMudar();
              }}
            />
            <p className="cg-nota">
              Restaurar cria uma nova alteração a partir da versão escolhida — a versão vigente
              continua no histórico. Nada é apagado.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

/* ------------------------------ aplicativo do balconista ------------------------------ */

const PROGRAMAS_DO_APP: readonly { chave: keyof AppDoBalconista['programas']; rotulo: string }[] = [
  { chave: 'vitamina', rotulo: 'Vitaminas' },
  { chave: 'pre_vencido', rotulo: 'Pré-vencidos' },
  { chave: 'paradao', rotulo: 'Paradão' },
];

/**
 * As chaves do 1.0: quais programas o balconista vê na venda e o MODO MANUTENÇÃO (recusa qualquer
 * lançamento com a mensagem). Quem decide é o servidor — desligar aqui vale na hora, em todo
 * celular, sem depender de a tela do balconista recarregar.
 */
function PainelDoBalconista({ sessao, aoMudar }: { sessao: Sessao; aoMudar: () => void }) {
  const [atual, setAtual] = useState<AppDoBalconista | null>(null);
  const [falhou, setFalhou] = useState(false);
  const [v, setV] = useState<Omit<AppDoBalconista, 'versao'> | null>(null);
  const [motivo, setMotivo] = useState('');
  const [d, setD] = useState<Desfecho>({ t: 'nada' });
  const carregar = () => {
    setFalhou(false);
    void lerAppDoBalconista().then((r) => {
      if (!r.ok) return setFalhou(true);
      setAtual(r.dados);
      setV({
        programas: r.dados.programas,
        manutencao: r.dados.manutencao,
        valores: r.dados.valores,
      });
    });
  };
  useEffect(carregar, []);
  if (falhou)
    return (
      <div className="cg-cartao">
        <ErroDeArea oQue="o aplicativo do balconista" aoTentar={carregar} />
      </div>
    );
  if (!atual || !v) return <div className="cg-cartao cg-carregando" aria-busy="true" />;
  const mudou =
    JSON.stringify(v.programas) !== JSON.stringify(atual.programas) ||
    v.manutencao.ativa !== atual.manutencao.ativa ||
    v.manutencao.mensagem.trim() !== atual.manutencao.mensagem ||
    v.valores !== atual.valores;
  const salvando = d.t === 'salvando';
  const salvar = () => {
    if (motivo.trim().length < 5)
      return setD({ t: 'falha', msg: 'Diga por que está mudando (mínimo 5 caracteres).' });
    setD({ t: 'salvando' });
    void salvarAppDoBalconista(sessao, {
      programas: v.programas,
      manutencao: { ativa: v.manutencao.ativa, mensagem: v.manutencao.mensagem.trim() },
      valores: v.valores,
      motivo: motivo.trim(),
      versaoAtual: atual.versao,
    }).then((r) => {
      if (r.tipo === 'confirmada') {
        setAtual(r.dados);
        setV({
          programas: r.dados.programas,
          manutencao: r.dados.manutencao,
          valores: r.dados.valores,
        });
        setMotivo('');
        setD({ t: 'salvo', as: hora(new Date().toISOString()), versao: r.dados.versao });
        aoMudar();
      } else if (r.tipo === 'recusada')
        setD(
          r.status === 409 ? { t: 'conflito', msg: r.mensagem } : { t: 'falha', msg: r.mensagem },
        );
      else setD({ t: 'incerta' });
    });
  };
  return (
    <div className="cg-cartao cg-geral">
      <div className="cg-cartao-topo cg-cartao-topo-linha">
        <h2>Aplicativo do balconista</h2>
        {atual.manutencao.ativa ? (
          <span className="cg-marca cg-marca-aviso">
            <span className="cg-ponto" />
            Em manutenção agora
          </span>
        ) : mudou ? (
          <span className="cg-marca cg-marca-aviso">
            <span className="cg-ponto" />
            Alterações não salvas
          </span>
        ) : null}
      </div>
      <div className="cg-geral-corpo">
        <div className="cg-rotulo">Programas na venda</div>
        <div className="cg-chaves">
          {PROGRAMAS_DO_APP.map((p) => (
            <label key={p.chave} className="cg-chave">
              <input
                type="checkbox"
                checked={v.programas[p.chave]}
                disabled={salvando}
                onChange={(e) =>
                  setV({ ...v, programas: { ...v.programas, [p.chave]: e.target.checked } })
                }
              />
              <span>{p.rotulo}</span>
            </label>
          ))}
        </div>
        <div className="cg-rotulo">Valores para o balconista</div>
        <div className="cg-botoes" role="group" aria-label="Valores para o balconista">
          {(
            [
              ['MOSTRAR', 'Mostrar valores'],
              ['SO_NUMEROS', 'Só números'],
            ] as const
          ).map(([chave, rotulo]) => (
            <button
              key={chave}
              type="button"
              className={v.valores === chave ? 'cg-primario' : 'cg-secundario'}
              aria-pressed={v.valores === chave}
              disabled={salvando}
              onClick={() => setV({ ...v, valores: chave })}
            >
              {rotulo}
            </button>
          ))}
        </div>
        <p className="cg-nota">
          Só números: o balconista não vê quanto ganha nem quanto vendeu em R$; preços dos produtos
          continuam.
        </p>
        <label className="cg-chave cg-chave-perigo">
          <input
            type="checkbox"
            checked={v.manutencao.ativa}
            disabled={salvando}
            onChange={(e) =>
              setV({ ...v, manutencao: { ...v.manutencao, ativa: e.target.checked } })
            }
          />
          <span>
            <b>Modo manutenção</b> — recusa todo lançamento do balconista com a mensagem abaixo
          </span>
        </label>
        {v.manutencao.ativa && (
          <label className="cg-motivo">
            <span className="cg-rotulo">Mensagem para o balconista</span>
            <input
              className="cg-input"
              value={v.manutencao.mensagem}
              maxLength={200}
              disabled={salvando}
              onChange={(e) =>
                setV({ ...v, manutencao: { ...v.manutencao, mensagem: e.target.value } })
              }
            />
          </label>
        )}
        {mudou && (
          <label className="cg-motivo">
            <span className="cg-rotulo">Por que está mudando? · fica no histórico</span>
            <input
              className="cg-input cg-input-motivo"
              value={motivo}
              maxLength={300}
              disabled={salvando}
              placeholder="Ex.: virada do 1.0 para o 2.0"
              onChange={(e) => setMotivo(e.target.value)}
            />
          </label>
        )}
        <div className="cg-botoes">
          <button
            type="button"
            className="cg-primario"
            disabled={!mudou || salvando}
            onClick={salvar}
          >
            {salvando ? 'Salvando…' : 'Salvar'}
          </button>
          <button
            type="button"
            className="cg-secundario"
            disabled={!mudou || salvando}
            onClick={() => {
              setV({
                programas: atual.programas,
                manutencao: atual.manutencao,
                valores: atual.valores,
              });
              setMotivo('');
              setD({ t: 'nada' });
            }}
          >
            Descartar
          </button>
        </div>
        <FaixaDoDesfecho
          d={d}
          aoRecarregar={() => {
            setD({ t: 'nada' });
            carregar();
          }}
        />
      </div>
    </div>
  );
}

/* ------------------------------ sincronizador do HOS (só os donos) ------------------------------ */

const SITUACAO_DO_HOS: Record<
  EstadoDoSincronizadorHos['situacao'],
  { texto: string; aviso: boolean }
> = {
  no_ar: { texto: 'Buscando no HOS', aviso: false },
  pausado: { texto: 'Busca pausada', aviso: true },
  parado: { texto: 'Sincronizador parado · sem sinal há mais de 3 min', aviso: true },
  nunca_rodou: { texto: 'Sincronizador ainda não rodou neste servidor', aviso: true },
};

type CamposDoHos = {
  vendasMin: string;
  catalogoMin: string;
  diasRelidos: string;
  pausado: boolean;
};
type PedidoAgora =
  | { readonly t: 'nada' }
  | { readonly t: 'pedindo' }
  | { readonly t: 'pedido' }
  | { readonly t: 'recusado'; readonly msg: string };

/** "2026-09-26" → "26/09/2026" (dia de calendário, sem fuso). */
const diaDaData = (d: string) => `${d.slice(8, 10)}/${d.slice(5, 7)}/${d.slice(0, 4)}`;

/** O último dia completo está antes de ontem: falta ler algum dia inteiro. */
function atrasado(completoAte: string | null, hoje: string): boolean {
  if (!completoAte) return false;
  const ontem = new Date(`${hoje}T12:00:00Z`);
  ontem.setUTCDate(ontem.getUTCDate() - 1);
  return completoAte < ontem.toISOString().slice(0, 10);
}

const real = (v: string) =>
  Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

/**
 * Devoluções do HOS que NÃO entraram no cheque (fracionada, sem valor, sem operador). O dono vê
 * cada uma e a dá como revisada dizendo o que fez — fica na trilha com quem revisou.
 */
function RevisaoDeDevolucoes({ sessao, aoRevisar }: { sessao: Sessao; aoRevisar: () => void }) {
  const [aberta, setAberta] = useState(false);
  const [lista, setLista] = useState<{
    total: number;
    itens: readonly DevolucaoParaRevisar[];
  } | null>(null);
  const [falhou, setFalhou] = useState(false);
  const [editando, setEditando] = useState<string | null>(null);
  const [texto, setTexto] = useState('');
  const [d, setD] = useState<Desfecho>({ t: 'nada' });
  const chave = (x: DevolucaoParaRevisar) => `${x.dia}|${x.vendaId}|${x.cupom}|${x.cod}`;
  const carregar = () => {
    void lerDevolucoesParaRevisar().then((r) => {
      if (!r.ok) return setFalhou(true);
      setFalhou(false);
      setLista(r.dados);
    });
  };
  useEffect(() => {
    if (aberta) carregar();
  }, [aberta]);
  const revisar = (x: DevolucaoParaRevisar) => {
    if (texto.trim().length < 5)
      return setD({ t: 'falha', msg: 'Diga o que foi feito (mínimo 5 caracteres).' });
    setD({ t: 'salvando' });
    void revisarDevolucao(sessao, {
      dia: x.dia,
      vendaId: x.vendaId,
      cupom: x.cupom,
      cod: x.cod,
      resolucao: texto.trim(),
    }).then((r) => {
      if (r.tipo === 'confirmada') {
        setEditando(null);
        setTexto('');
        setD({ t: 'nada' });
        carregar();
        aoRevisar();
      } else if (r.tipo === 'recusada') setD({ t: 'falha', msg: r.mensagem });
      else setD({ t: 'incerta' });
    });
  };
  if (!aberta)
    return (
      <div className="cg-botoes">
        <button type="button" className="cg-secundario" onClick={() => setAberta(true)}>
          Ver devoluções para revisar
        </button>
      </div>
    );
  if (falhou && !lista) return <ErroDeArea oQue="as devoluções para revisar" aoTentar={carregar} />;
  if (!lista) return <div className="cg-carregando" aria-busy="true" />;
  return (
    <section className="cg-rev" aria-label="Devoluções para revisar">
      <div className="cg-nota">
        Estas devoluções vieram do HOS mas <b>não entraram no cheque</b>. Veja no HOS, acerte o que
        precisar e dê como revisada dizendo o que fez.
        {lista.total > lista.itens.length &&
          ` Mostrando ${String(lista.itens.length)} de ${lista.total.toLocaleString('pt-BR')}.`}
      </div>
      {lista.itens.length === 0 && (
        <div className="cg-nota">Nenhuma devolução esperando revisão.</div>
      )}
      <ul className="cg-rev-lista">
        {lista.itens.map((x) => {
          const k = chave(x);
          const salvando = d.t === 'salvando' && editando === k;
          return (
            <li key={k} className="cg-rev-item">
              <div className="cg-rev-dados">
                <span className="cg-rev-dia">{diaDaData(x.dia)}</span>
                <span>
                  <b>{x.nome ?? 'Produto sem nome'}</b> · cód. {x.cod}
                </span>
                <span>
                  cupom {x.cupom} · {x.qtd.toLocaleString('pt-BR')} un ·{' '}
                  {x.valor === null ? 'sem valor' : real(x.valor)}
                </span>
                <span>
                  vendedor {x.vendedor ?? '—'} · devolveu {x.opDevol ?? '—'}
                </span>
                <span className="cg-rev-motivo">{x.motivo}</span>
              </div>
              {editando === k ? (
                <div className="cg-rev-acao cg-rev-editando">
                  <input
                    className="cg-input"
                    value={texto}
                    maxLength={300}
                    disabled={salvando}
                    placeholder="O que foi feito? Ex.: acertei no cheque à mão"
                    onChange={(ev) => setTexto(ev.target.value)}
                  />
                  <button
                    type="button"
                    className="cg-primario"
                    disabled={salvando}
                    onClick={() => revisar(x)}
                  >
                    {salvando ? 'Salvando…' : 'Confirmar'}
                  </button>
                  <button
                    type="button"
                    className="cg-secundario"
                    disabled={salvando}
                    onClick={() => {
                      setEditando(null);
                      setD({ t: 'nada' });
                    }}
                  >
                    Cancelar
                  </button>
                </div>
              ) : (
                <div className="cg-rev-acao">
                  <button
                    type="button"
                    className="cg-secundario"
                    onClick={() => {
                      setEditando(k);
                      setTexto('');
                      setD({ t: 'nada' });
                    }}
                  >
                    Dar como revisada
                  </button>
                </div>
              )}
              {editando === k && d.t === 'falha' && (
                <div className="cg-estado cg-estado-aviso" role="alert">
                  <span>{d.msg}</span>
                </div>
              )}
              {editando === k && d.t === 'incerta' && (
                <div className="cg-estado cg-estado-aviso" role="alert">
                  <span>
                    Sem resposta do servidor: pode ter sido salvo. Recarregue antes de repetir.
                  </span>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function PainelDoHos({ sessao, aoMudar }: { sessao: Sessao; aoMudar: () => void }) {
  const [e, setE] = useState<EstadoDoSincronizadorHos | null>(null);
  const [falhou, setFalhou] = useState(false);
  const [v, setV] = useState<CamposDoHos | null>(null);
  const [motivo, setMotivo] = useState('');
  const [d, setD] = useState<Desfecho>({ t: 'nada' });
  const [agora, setAgora] = useState<PedidoAgora>({ t: 'nada' });
  const campos = (x: EstadoDoSincronizadorHos): CamposDoHos => ({
    vendasMin: String(x.configuracao.vendasMin),
    catalogoMin: String(x.configuracao.catalogoMin),
    diasRelidos: String(x.configuracao.diasRelidos),
    pausado: x.configuracao.pausado,
  });
  const carregar = (tambemCampos: boolean) => {
    void lerSincronizadorHos().then((r) => {
      if (!r.ok) return setFalhou(true);
      setFalhou(false);
      setE(r.dados);
      if (tambemCampos) setV(campos(r.dados));
    });
  };
  useEffect(() => {
    carregar(true);
  }, []);
  useEffect(() => {
    // o estado (última busca, próxima, em andamento) se atualiza sozinho; os campos não mudam.
    // Revisão 360: a cada 60 s (15 s logo depois de "Buscar agora") e NUNCA com a aba escondida;
    // voltar para a aba relê na hora.
    const passo = agora.t === 'pedido' ? 15_000 : 60_000;
    const t = window.setInterval(() => {
      if (!document.hidden) carregar(false);
    }, passo);
    const voltou = () => {
      if (document.visibilityState === 'visible') carregar(false);
    };
    document.addEventListener('visibilitychange', voltou);
    return () => {
      window.clearInterval(t);
      document.removeEventListener('visibilitychange', voltou);
    };
  }, [agora.t]);
  if (falhou && !e)
    return (
      <div className="cg-cartao">
        <ErroDeArea oQue="a busca no HOS" aoTentar={() => carregar(true)} />
      </div>
    );
  if (!e || !v) return <div className="cg-cartao cg-carregando" aria-busy="true" />;
  const c = e.configuracao;
  const mudou =
    v.vendasMin !== String(c.vendasMin) ||
    v.catalogoMin !== String(c.catalogoMin) ||
    v.diasRelidos !== String(c.diasRelidos) ||
    v.pausado !== c.pausado;
  const salvando = d.t === 'salvando';
  const sit = SITUACAO_DO_HOS[e.situacao];
  const quando = (iso: string | null) => (iso ? diaHora(iso) : '—');
  const salvar = () => {
    const n = {
      vendasMin: Number(v.vendasMin),
      catalogoMin: Number(v.catalogoMin),
      diasRelidos: Number(v.diasRelidos),
    };
    for (const [k, nome] of [
      ['vendasMin', 'Vendas'],
      ['catalogoMin', 'Produtos'],
      ['diasRelidos', 'Dias relidos'],
    ] as const) {
      const lim = e.limites[k];
      if (!Number.isInteger(n[k]) || n[k] < lim.min || n[k] > lim.max)
        return setD({
          t: 'falha',
          msg: `${nome}: um número inteiro de ${String(lim.min)} a ${String(lim.max)}.`,
        });
    }
    if (motivo.trim().length < 5)
      return setD({ t: 'falha', msg: 'Diga por que está mudando (mínimo 5 caracteres).' });
    setD({ t: 'salvando' });
    void salvarSincronizadorHos(sessao, {
      ...n,
      pausado: v.pausado,
      motivo: motivo.trim(),
      versaoAtual: c.versao,
    }).then((r) => {
      if (r.tipo === 'confirmada') {
        setMotivo('');
        setD({ t: 'salvo', as: hora(new Date().toISOString()), versao: r.dados.versao });
        carregar(true);
        aoMudar();
      } else if (r.tipo === 'recusada')
        setD(
          r.status === 409 ? { t: 'conflito', msg: r.mensagem } : { t: 'falha', msg: r.mensagem },
        );
      else setD({ t: 'incerta' });
    });
  };
  const buscarAgora = () => {
    setAgora({ t: 'pedindo' });
    void buscarAgoraNoHos(sessao).then((r) => {
      if (r.tipo === 'confirmada') {
        setAgora({ t: 'pedido' });
        carregar(false);
      } else if (r.tipo === 'recusada') setAgora({ t: 'recusado', msg: r.mensagem });
      else
        setAgora({
          t: 'recusado',
          msg: 'Sem resposta do servidor: o pedido pode ter sido registrado. Recarregue antes de repetir.',
        });
    });
  };
  const numero = (
    k: 'vendasMin' | 'catalogoMin' | 'diasRelidos',
    rotulo: string,
    unidade: string,
  ) => (
    <label className="cg-motivo">
      <span className="cg-rotulo">{rotulo}</span>
      <span className="cg-hos-numero">
        <input
          className="cg-input"
          type="number"
          inputMode="numeric"
          min={e.limites[k].min}
          max={e.limites[k].max}
          step={1}
          value={v[k]}
          disabled={salvando}
          onChange={(x) => setV({ ...v, [k]: x.target.value })}
        />
        <span>{unidade}</span>
      </span>
    </label>
  );
  return (
    <div className="cg-cartao cg-geral">
      <div className="cg-cartao-topo cg-cartao-topo-linha">
        <h2>Busca no HOS</h2>
        <span className={`cg-marca${sit.aviso ? ' cg-marca-aviso' : ''}`}>
          <span className={`cg-ponto${sit.aviso ? '' : ' cg-hos-ponto-ok'}`} />
          {e.emExecucao ? 'Buscando agora…' : sit.texto}
        </span>
      </div>
      <div className="cg-geral-corpo">
        <dl className="cg-hos-estado">
          <dt>Última busca de vendas</dt>
          <dd>
            {quando(e.ultimaBusca.fim)}
            {e.ultimaBusca.ok === true && ' · deu certo'}
            {e.ultimaBusca.ok === false && <b className="cg-hos-erro"> · com problema</b>}
          </dd>
          {e.ultimaBusca.erro && (
            <>
              <dt>Último problema</dt>
              <dd className="cg-hos-erro">{e.ultimaBusca.erro}</dd>
            </>
          )}
          <dt>Última busca de produtos</dt>
          <dd>
            {quando(e.ultimaBuscaDeProdutos.fim)}
            {e.ultimaBuscaDeProdutos.ok === false && <b className="cg-hos-erro"> · com problema</b>}
          </dd>
          <dt>Próxima busca</dt>
          <dd>{e.buscaAgoraPendente ? 'em instantes (pedida agora)' : quando(e.proximaBusca)}</dd>
          <dt>Hoje no InovaPay</dt>
          <dd>
            {e.hoje.vendas.toLocaleString('pt-BR')} venda(s) do HOS ·{' '}
            {e.hoje.devolucoes.toLocaleString('pt-BR')} devolução(ões)
            {e.hoje.ultimaVenda && ` · última às ${hora(e.hoje.ultimaVenda)}`}
          </dd>
          <dt>Vendas completas até</dt>
          <dd>
            {e.vendasCompletasAte ? diaDaData(e.vendasCompletasAte) : '—'}
            {atrasado(e.vendasCompletasAte, e.hoje.dia) && (
              <b className="cg-hos-erro"> · ontem ainda não foi lido por completo</b>
            )}
          </dd>
          <dt>Devoluções para revisar</dt>
          <dd>
            {e.devolucoesParaRevisar === 0 ? (
              'nenhuma'
            ) : (
              <b className="cg-hos-erro">
                {e.devolucoesParaRevisar.toLocaleString('pt-BR')} fora do cheque
              </b>
            )}
          </dd>
        </dl>
        {e.devolucoesParaRevisar > 0 && (
          <RevisaoDeDevolucoes sessao={sessao} aoRevisar={() => carregar(false)} />
        )}
        <div className="cg-botoes">
          <button
            type="button"
            className="cg-secundario"
            disabled={c.pausado || agora.t === 'pedindo' || e.buscaAgoraPendente || e.emExecucao}
            onClick={buscarAgora}
          >
            {agora.t === 'pedindo' ? 'Pedindo…' : 'Buscar agora'}
          </button>
        </div>
        {agora.t === 'pedido' && (
          <div className="cg-estado cg-estado-ok" role="status">
            <span>Pedido registrado · o sincronizador começa em até 10 segundos.</span>
          </div>
        )}
        {agora.t === 'recusado' && (
          <div className="cg-estado cg-estado-aviso" role="alert">
            <span>{agora.msg}</span>
          </div>
        )}
        <div className="cg-hos-campos">
          {numero('vendasMin', 'Buscar vendas a cada', 'min')}
          {numero('catalogoMin', 'Buscar produtos a cada', 'min')}
          {numero('diasRelidos', 'Reler dias para trás', 'dia(s)')}
        </div>
        <div className="cg-nota">
          Vendas trazem junto devoluções, cancelamentos e custo dos itens. Produtos = cadastro,
          estoque e preço. Reler dias para trás pega venda que o HOS lança atrasada.
        </div>
        <label className="cg-chave cg-chave-perigo">
          <input
            type="checkbox"
            checked={v.pausado}
            disabled={salvando}
            onChange={(x) => setV({ ...v, pausado: x.target.checked })}
          />
          <span>
            <b>Pausar a busca</b> — o InovaPay para de buscar no HOS até religar (use em manutenção
            do HOS)
          </span>
        </label>
        {mudou && (
          <label className="cg-motivo">
            <span className="cg-rotulo">Por que está mudando? · fica no histórico</span>
            <input
              className="cg-input cg-input-motivo"
              value={motivo}
              maxLength={300}
              disabled={salvando}
              placeholder="Ex.: loja cheia, buscar mais rápido"
              onChange={(x) => setMotivo(x.target.value)}
            />
          </label>
        )}
        <div className="cg-botoes">
          <button
            type="button"
            className="cg-primario"
            disabled={!mudou || salvando}
            onClick={salvar}
          >
            {salvando ? 'Salvando…' : 'Salvar'}
          </button>
          <button
            type="button"
            className="cg-secundario"
            disabled={!mudou || salvando}
            onClick={() => {
              setV(campos(e));
              setMotivo('');
              setD({ t: 'nada' });
            }}
          >
            Descartar
          </button>
        </div>
        <FaixaDoDesfecho
          d={d}
          aoRecarregar={() => {
            setD({ t: 'nada' });
            carregar(true);
          }}
        />
      </div>
    </div>
  );
}
